import { useInfiniteQuery } from '@tanstack/react-query';
import { api, GuildActivityEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildActivityReturn {
  activities: GuildActivityEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useGuildActivity(guildId: string | undefined): UseGuildActivityReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.guilds.activity(guildId!),
      queryFn: ({ pageParam = 0 }) =>
        api.listGuildActivity(guildId!, { limit: LIMIT, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
      enabled: !!guildId,
    });

  const activities = data?.pages.flatMap((page) => page.activities) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    activities,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load activity' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
  };
}
